import React, { useRef } from "react";
import CountUp from "react-countup";
import { gsap, useGSAP, MOTION_OK } from "../../lib/gsap";
import { projectData } from "../../data/projects";
import { WHATSAPP } from "../../data/site";
import { scrollToId } from "../../lib/scroll";
import { ArrowIcon } from "./Chrome";
import portrait from "../../assets/images/sami.webp";

const NAME = "SAMI";
const ROLES = ["CTO", "AI Builder", "Tech Partner"];
const LIVE_COUNT = projectData.filter((p) => p.appStore || p.playStore).length;

const STATS = [
  { value: 10, suffix: "+", label: "Years of experience" },
  { value: 50, suffix: "+", label: "Products delivered" },
  { value: LIVE_COUNT, suffix: "", label: "Apps live in stores" },
  { value: 5, suffix: "", label: "Countries served" },
];

/* The hero. Home renders it twice: once at the top, and once more (`clone`) as
   the last screen of the page, so that scrolling past the end can wrap back to
   the start without anyone seeing the join. The copy must look identical, so it
   runs the same animations; it only drops the id, the count-up and tab stops. */
function Landing({ clone = false }) {
  const root = useRef(null);
  const tabIndex = clone ? -1 : undefined;

  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add(MOTION_OK, () => {
        const intro = gsap.timeline({ defaults: { ease: "power4.out" } });
        intro
          .from(".landing-intro .char", { yPercent: 110, duration: 1.2, stagger: 0.07 })
          .from(".landing-fade", { y: 24, autoAlpha: 0, duration: 1, stagger: 0.08 }, 0.4);

        // Cycle the role words through the masked slot on the right
        const words = gsap.utils.toArray(".role-stack span");
        gsap.set(words, { yPercent: 100, autoAlpha: 1 });
        const cycle = gsap.timeline({ repeat: -1, delay: 0.6 });
        words.forEach((word) => {
          cycle
            .to(word, { yPercent: 0, duration: 0.8, ease: "power4.out" })
            .to(word, { yPercent: -100, duration: 0.7, ease: "power3.in" }, "+=1.8")
            .set(word, { yPercent: 100 });
        });

        // Ease the hero away as the page scrolls past it
        gsap.to(".landing-grid", {
          yPercent: -14,
          autoAlpha: 0.15,
          ease: "none",
          scrollTrigger: { trigger: root.current, start: "top top", end: "bottom top", scrub: true },
        });
      });
      return () => mm.revert();
    },
    { scope: root }
  );

  return (
    <section id={clone ? undefined : "hero"} className="landing" ref={root}>
      <div className="landing-glow" />
      <div className="wrap">
        <div className="landing-grid">
          <div className="landing-intro">
            <h2 className="landing-fade">Hello! I&rsquo;m</h2>
            <h1 aria-label="Sami — fractional CTO and AI product builder for startups in the UAE, Saudi Arabia and Pakistan">
              <span className="mask" aria-hidden="true">
                {NAME.split("").map((c, i) => (
                  <span key={i} className="char">
                    {c}
                  </span>
                ))}
              </span>
            </h1>
            <p className="landing-fade">
              I help founders across the UAE, Saudi Arabia and the wider Gulf ship AI-powered mobile apps,
              web platforms and automation — from first idea to App Store launch.
            </p>
          </div>

          {/* AvatarStage draws the 3D avatar over this slot; the photo only shows without WebGL */}
          <div
            className="avatar-slot avatar-slot--hero"
            data-avatar-slot={clone ? "loop" : "hero"}
            role="img"
            aria-label="3D avatar of Sami that follows your cursor"
          >
            <img className="avatar-fallback" src={portrait} alt="" loading="lazy" width="720" height="992" />
          </div>

          <div className="landing-roles">
            <h3 className="landing-fade">A Fractional</h3>
            <div className="role-stack" aria-label={ROLES.join(", ")}>
              {ROLES.map((r) => (
                <span key={r} aria-hidden="true">
                  {r}
                </span>
              ))}
            </div>
            <p className="role-sub landing-fade">Based in Lahore · GMT+5</p>
            <div className="landing-ctas landing-fade">
              <button type="button" className="btn btn--solid" tabIndex={tabIndex} onClick={() => scrollToId("work")}>
                View my work
              </button>
              <a href={WHATSAPP} target="_blank" rel="noopener noreferrer" className="btn" tabIndex={tabIndex}>
                Book a call <ArrowIcon />
              </a>
            </div>
          </div>
        </div>

        <div className="landing-stats">
          {STATS.map((s) => (
            <div key={s.label} className="stat landing-fade">
              <strong>
                {clone ? s.value : <CountUp end={s.value} duration={2.4} enableScrollSpy scrollSpyOnce />}
                {s.suffix}
              </strong>
              <span>{s.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default Landing;
